import { useRef, useState } from "react";
import { Printer, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { cetak, resolveDim, rp } from "./FakturPrint";
import { formatTanggal } from "../../lib/tanggal";

// Surat Tagihan — hasil "Tagih Sekaligus" di halaman Piutang Usaha.
//
// SATU SURAT = SATU PELANGGAN (brief owner 29 Agu poin 21). Backend menolak
// permintaan yang mencampur beberapa pelanggan, jadi `dokumen.pelanggan`
// praktis selalu berisi satu; perulangan di bawah tetap dipertahankan supaya
// komponen ini tidak pecah kalau aturan itu berubah.
//
// KENAPA KOMPONEN SENDIRI
// -----------------------
// FakturPrint mencetak SATU transaksi berisi barang. Surat tagihan mencetak
// beberapa invoice, dan yang penting justru bukan barangnya melainkan umur
// tunggakannya. Memaksanya lewat FakturPrint berarti memalsukan tagihan jadi
// faktur, dan setiap perubahan tata letak faktur nanti akan menggeser surat
// tagihan tanpa ada yang berniat.
//
// Yang DIPAKAI ULANG adalah `cetak()` — ia menyalin `.print-area` ke
// `#print-root` sebagai anak langsung <body>. Memakai `window.print()` mentah
// akan melahirkan kembali bug "keluar 3-4 rangkap" (lihat CLAUDE.md).

export default function SuratTagihanPrint({ dokumen, template }) {
  const ref = useRef(null);
  const [jpgBusy, setJpgBusy] = useState(false);
  const t = template || {};
  const kop = t.kop || {};
  const dim = resolveDim(t.ukuran_kertas || "A4", t.orientasi || "");
  const pelanggan = dokumen?.pelanggan || [];

  /** Ekspor JPG (brief poin 22) — supaya suratnya bisa langsung dikirim lewat
   *  WhatsApp tanpa pelanggan perlu membuka PDF.
   *
   *  `html2canvas` diimpor DINAMIS: pustakanya ~50 KB dan hanya dipakai kalau
   *  tombol ini ditekan. Mengimpornya di atas berarti setiap pengguna Piutang
   *  mengunduhnya walau tidak pernah menagih. */
  const unduhJpg = async () => {
    if (!ref.current) return;
    setJpgBusy(true);
    try {
      const { default: html2canvas } = await import("html2canvas");
      const surat = ref.current.querySelectorAll("[data-surat]");
      const target = surat.length ? surat : [ref.current];
      for (let i = 0; i < target.length; i++) {
        // scale 2 supaya angka nominal tetap terbaca saat gambar diperkecil
        // aplikasi chat.
        const canvas = await html2canvas(target[i], { scale: 2, backgroundColor: "#ffffff" });
        const nama = (pelanggan[i]?.pelanggan_nama || "tagihan")
          .replace(/[^\w\s-]/g, "").trim().replace(/\s+/g, "-").slice(0, 40);
        const a = document.createElement("a");
        a.href = canvas.toDataURL("image/jpeg", 0.92);
        a.download = `Tagihan-${nama}-${(dokumen?.tanggal_penagihan || "").replace(/-/g, "")}.jpg`;
        a.click();
      }
      toast.success(target.length > 1 ? `${target.length} gambar diunduh` : "Gambar diunduh");
    } catch (e) {
      toast.error("Gagal membuat gambar: " + (e?.message || "tidak diketahui"));
    } finally {
      setJpgBusy(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <p className="text-xs text-slate-500">
          {dokumen?.jumlah_invoice} invoice · Grand Total {rp(dokumen?.grand_total || 0)}
          <span className="block text-[11px] text-slate-400 mt-0.5">
            PDF lewat dialog cetak (pilih tujuan &quot;Save as PDF&quot;), atau unduh JPG untuk dikirim via chat.
          </span>
        </p>
        <div className="flex items-center gap-2">
          <button onClick={unduhJpg} disabled={jpgBusy} data-testid="tagihan-jpg-tombol"
                  className="h-10 px-4 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-50 text-sm font-medium inline-flex items-center gap-2">
            <ImageIcon className="w-4 h-4" /> {jpgBusy ? "Membuat..." : "Unduh JPG"}
          </button>
          <button onClick={() => cetak(ref.current)} data-testid="tagihan-cetak-tombol"
                  className="h-10 px-4 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium inline-flex items-center gap-2">
            <Printer className="w-4 h-4" /> Cetak / Simpan PDF
          </button>
        </div>
      </div>

      <div ref={ref} className="print-area" data-testid="tagihan-print-area">
        {pelanggan.map((g, gi) => (
          <div key={g.pelanggan_nama + gi} data-surat
               className="bg-white text-slate-900 mx-auto shadow-sm border border-slate-200 mb-4"
               style={{
                 width: dim?.width ? `${dim.width}mm` : "210mm",
                 minHeight: dim?.height ? `${dim.height}mm` : undefined,
                 padding: "16mm 14mm",
                 // Surat berikutnya SELALU mulai di lembar baru.
                 breakAfter: gi < pelanggan.length - 1 ? "page" : "auto",
                 pageBreakAfter: gi < pelanggan.length - 1 ? "always" : "auto",
               }}
               data-testid={`tagihan-surat-${gi}`}>
            <div className="flex items-start gap-4 border-b-2 border-slate-900 pb-3">
              {kop.tampil_logo !== false && kop.logo_url && (
                <img src={kop.logo_url} alt="" style={{ maxHeight: "56px" }} />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-lg font-bold leading-tight">{kop.judul || "CV ZOA INTERSTELLAR GROUP"}</p>
                {kop.alamat && <p className="text-[11px] leading-snug">{kop.alamat}</p>}
                {kop.telepon && <p className="text-[11px] leading-snug">{kop.telepon}</p>}
              </div>
            </div>

            <div className="text-center my-5">
              <p className="text-base font-bold tracking-widest">SURAT TAGIHAN</p>
              <p className="text-xs mt-0.5">
                Tanggal Penagihan: {formatTanggal(dokumen?.tanggal_penagihan)}
              </p>
            </div>

            <table className="text-xs mb-4">
              <tbody>
                <tr>
                  <td className="pr-3 align-top py-0.5 whitespace-nowrap">Kepada Yth.</td>
                  <td className="pr-1 align-top py-0.5">:</td>
                  <td className="align-top py-0.5 font-semibold">{g.pelanggan_nama}</td>
                </tr>
                {g.no_hp && (
                  <tr>
                    <td className="pr-3 align-top py-0.5">Kontak</td>
                    <td className="pr-1 align-top py-0.5">:</td>
                    <td className="align-top py-0.5">{g.no_hp}</td>
                  </tr>
                )}
              </tbody>
            </table>

            <p className="text-xs leading-relaxed mb-3">
              Bersama ini kami sampaikan rincian tagihan yang masih tercatat atas nama Bapak/Ibu.
              Mohon kesediaannya untuk menyelesaikan pembayaran sesuai tanggal jatuh tempo di bawah ini.
            </p>

            <table className="w-full text-[11px] border-collapse">
              <thead>
                <tr className="border-y border-slate-400">
                  <th className="text-left py-1.5 px-1">No. Invoice</th>
                  <th className="text-left py-1.5 px-1">Tgl Pembelian</th>
                  <th className="text-left py-1.5 px-1">Jatuh Tempo</th>
                  <th className="text-right py-1.5 px-1">Total Pembelian</th>
                  <th className="text-right py-1.5 px-1">Sudah Dibayar</th>
                  <th className="text-right py-1.5 px-1">Sisa Tagihan</th>
                </tr>
              </thead>
              <tbody>
                {(g.invoices || []).map(inv => (
                  <tr key={inv.id} className="border-b border-slate-200">
                    <td className="py-1.5 px-1 font-mono">{inv.code}</td>
                    <td className="py-1.5 px-1">{formatTanggal(inv.tanggal)}</td>
                    <td className="py-1.5 px-1">
                      {inv.tanggal_jatuh_tempo ? formatTanggal(inv.tanggal_jatuh_tempo) : "-"}
                      {inv.terlambat && (
                        <span className="block text-[10px] font-semibold">
                          terlambat {inv.hari_terlambat} hari
                        </span>
                      )}
                    </td>
                    <td className="py-1.5 px-1 text-right font-mono">{rp(inv.nominal)}</td>
                    <td className="py-1.5 px-1 text-right font-mono">{rp(inv.nominal_terbayar || 0)}</td>
                    <td className="py-1.5 px-1 text-right font-mono">{rp(inv.sisa)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-slate-400">
                  <td className="py-1 px-1" colSpan={3}>Total Pembelian</td>
                  <td className="py-1 px-1 text-right font-mono">{rp(g.total_pembelian || 0)}</td>
                  <td className="py-1 px-1 text-right font-mono">{rp(g.total_terbayar || 0)}</td>
                  <td className="py-1 px-1 text-right font-mono">{rp(g.total_sisa)}</td>
                </tr>
                <tr className="border-y-2 border-slate-900 font-bold">
                  <td className="py-2 px-1" colSpan={5}>GRAND TOTAL YANG HARUS DIBAYAR</td>
                  <td className="py-2 px-1 text-right font-mono">{rp(g.total_sisa)}</td>
                </tr>
              </tfoot>
            </table>

            {g.terlambat > 0 && (
              <p className="text-[11px] mt-3 font-semibold">
                {g.terlambat} dari {g.invoices.length} invoice di atas sudah melewati tanggal jatuh tempo.
              </p>
            )}

            {/* Rekening tujuan per CABANG. Cabang yang rekeningnya belum diisi
                TIDAK disamarkan dengan rekening cabang lain — kalau disamarkan,
                pelanggan mentransfer ke kas cabang yang tidak menjual barangnya
                dan uangnya masuk pembukuan yang salah.

                F9 (brief owner 12 Sep): kalau rekening cabang kosong, dipakai
                rekening ZOA GROUP sebagai CADANGAN — dan disebut apa adanya
                ("rekening pusat"), bukan dicetak seolah-olah rekening cabang
                itu. Begitu rekening cabangnya diisi, cadangan ini otomatis
                mundur. Rekening cabang LAIN tetap tidak pernah dipakai
                menambal cabang yang kosong. */}
            <div className="mt-4 border border-slate-300 rounded p-3">
              <p className="text-[11px] font-bold mb-1">INFORMASI PEMBAYARAN</p>
              {(g.rekening || []).length === 0 ? (
                <p className="text-[11px]">Hubungi kami untuk informasi rekening pembayaran.</p>
              ) : (
                <table className="text-[11px] w-full">
                  <tbody>
                    {g.rekening.map(r => (
                      <tr key={r.cabang}>
                        <td className="py-0.5 pr-3 whitespace-nowrap">{r.cabang}</td>
                        <td className="py-0.5">
                          {r.nomor
                            ? <>
                                {r.bank} <span className="font-mono font-semibold">{r.nomor}</span>
                                {r.atas_nama && <> a.n. {r.atas_nama}</>}
                                {r.pakai_pusat && (
                                  <span className="italic"> (rekening pusat {r.atas_nama_cabang})</span>
                                )}
                              </>
                            : <span className="italic">rekening cabang ini belum diisi di sistem</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {dokumen?.catatan && (
              <p className="text-[11px] mt-3 leading-relaxed whitespace-pre-wrap">{dokumen.catatan}</p>
            )}

            <div className="flex justify-end mt-8">
              <div className="text-center text-[11px]">
                <p>Hormat kami,</p>
                <div style={{ height: "18mm" }} />
                <p className="border-t border-slate-400 pt-1 px-6">{dokumen?.dibuat_oleh || "-"}</p>
                <p className="text-[10px]">Bagian Keuangan</p>
              </div>
            </div>

            {t.footer_teks && (
              <p className="text-[10px] text-center mt-6 leading-snug whitespace-pre-wrap">{t.footer_teks}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
